(function (global) {
  'use strict';

  /*
    VCC condition-note cleanup.

    These corrections apply only to the extracted legacy sizing-chart condition
    note. They must not be used as broad grammar correction for full product
    descriptions.
  */
  const CONFIG = {
    finalPhotoInstruction: 'SEE PHOTOS',
    maxTileDetailLength: 88,
    introductoryPhrasePattern: /see\s+sizing\s+chart\s+for\s+more\s+information/i,
    introWithSeparatorPattern: /(?:^|[\s>*\u2022-])\*?\s*see\s+sizing\s+chart\s+for\s+more\s+information\s*\.?\s*[-\u2013\u2014]\s*/i,
    typoCorrections: [
      [/\bsings?\s+of\s+wear\b/gi, 'Signs of wear'],
      [/\bsign\s+of\s+wear\b/gi, 'Signs of wear'],
      [/\bsigns?\s+of\s+war\b/gi, 'Signs of wear'],
      [/\bsigns?\s+of\s+wee\b/gi, 'Signs of wear'],
      [/\bfainted\s+(logo|graphic)\b/gi, 'faded $1'],
      [/\bfainted\s+screen[-\s]?print\b/gi, 'faded screen print'],
      [/\bbend\s+brim\b/gi, 'bent brim'],
      [/\bwaby\s+brim\b/gi, 'wavy brim'],
      [/\brepeat\s+washed\b/gi, 'repeated washing'],
      [/\bwarn\s+on\s+collar\b/gi, 'worn collar']
    ],
    phraseCorrections: [
      [/\bshow\s+aging\s+on\s+screen[-\s]?print\b/gi, 'aging on the screen print'],
      [/\bshown\s+aging\s+on\s+screen[-\s]?print\b/gi, 'aging on the screen print'],
      [/\bshows\s+aging\s+on\s+screen[-\s]?print\b/gi, 'aging on the screen print'],
      [/\bshowing\s+aging\s+on\s+screen[-\s]?print\b/gi, 'aging on the screen print'],
      [/\bscreen[-\s]?print\b/gi, 'screen print'],
      [/\bworn[-\s]?out\b/gi, 'worn-out']
    ],
    photoInstructionPatterns: [
      /\bsee\s+(?:p?hoto|poto|hoto|photos|p?hotos)\b\.?/gi,
      /\bse\s+photos?\b\.?/gi,
      /\b(?:as\s+)?seen\s+on\s+photos?\b\.?/gi,
      /\bshown?\s+on\s+photos?\b\.?/gi,
      /\bshown?\s+in\s+photos?\b\.?/gi
    ],
    genericTilePhrases: [
      /^(?:light|minor|heavy|visible)?\s*signs?\s+of\s+age\s*(?:and|or|&)\s*wear\b\s*(?:,|and)?\s*/i,
      /^(?:light|minor|heavy|visible)?\s*signs?\s+of\s+wear\b\s*(?:,|and)?\s*/i,
      /^(?:light|minor|heavy|visible)?\s*signs?\s+of\s+age\b\s*(?:,|and)?\s*/i,
      /^visible\s+vintage\s+wear\b\s*(?:,|and)?\s*/i,
      /^heavy\s+visible\s+vintage\s+wear\b\s*(?:,|and)?\s*/i
    ],
    recognizedConditionPattern: /\b(signs?\s+of\s+(?:age|wear)|wear|worn|mark|marks|stain|stains|hole|holes|tear|tears|repair|repaired|spot|spots|discoloration|fade|faded|fading|crack|cracked|aging|brim|collar|cuff|cuffs|sleeve|sleeves|print|logo|elastic|zipper|button|buttons|pocket|pockets|lining|shoulder|hem|stitch|fray|fraying|loose|missing|broken|pilling|yellowing|bleed|bleeding|distress|distressed|thinning|pinhole|pinholes)\b/i,
    safeStopWords: new Set([
      'a', 'an', 'and', 'as', 'at', 'for', 'in', 'is', 'it', 'of', 'on', 'or',
      'see', 'seen', 'photo', 'photos', 'the', 'to', 'with'
    ])
  };

  const cleanSpaces = (value) => String(value == null ? '' : value).replace(/\s+/g, ' ').trim();

  const decodeEntities = (value) => {
    const text = String(value == null ? '' : value);
    if (typeof document !== 'undefined' && document.createElement) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    }
    return text
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#039;|&apos;/gi, "'");
  };

  const stripHtml = (html) => decodeEntities(String(html == null ? '' : html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|li|div|tr|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, ' '));

  const cleanupDescriptionHtml = (html) => String(html == null ? '' : html)
    .replace(/<p\b[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/<li\b[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/li>/gi, '')
    .replace(/<div\b[^>]*>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/div>/gi, '')
    .replace(/(?:\s*<br\s*\/?>\s*){2,}/gi, '<br>')
    .replace(/(<p\b[^>]*>\s*)<br\s*\/?>/gi, '$1')
    .replace(/<br\s*\/?>\s*(<\/p>)/gi, '$1')
    .replace(/\n[ \t]*\n[ \t]*\n+/g, '\n\n')
    .trim();

  const extractConditionFromText = (text) => {
    const readable = cleanSpaces(stripHtml(text));
    const match = readable.match(CONFIG.introWithSeparatorPattern);
    if (!match) return null;
    const rawConditionText = cleanSpaces(readable.slice(match.index + match[0].length));
    return {
      rawMatchedLine: readable,
      rawConditionText
    };
  };

  const collectBlockCandidates = (html) => {
    const candidates = [];
    const blockPattern = /<(p|li|div)\b[^>]*>[\s\S]*?<\/\1>/gi;
    let match;

    while ((match = blockPattern.exec(html))) {
      const fullHtml = match[0];
      const tag = match[1].toLowerCase();
      const text = cleanSpaces(stripHtml(fullHtml));
      if (!CONFIG.introductoryPhrasePattern.test(text)) continue;
      if (tag === 'div' && (text.length > 360 || /<(?:p|li|div|table|tr|br)\b/i.test(fullHtml.replace(/^<div\b[^>]*>|<\/div>$/gi, '')))) continue;

      const extracted = extractConditionFromText(text);
      if (extracted) {
        candidates.push({
          start: match.index,
          end: match.index + fullHtml.length,
          html: fullHtml,
          ...extracted
        });
      }
    }

    return candidates;
  };

  const collectLineCandidates = (html) => {
    const candidates = [];
    const separatorPattern = /<br\s*\/?>|\r?\n/gi;
    let start = 0;
    let match;

    const pushSegment = (end) => {
      if (end <= start) return;
      const segment = html.slice(start, end);
      const text = cleanSpaces(stripHtml(segment));
      if (!CONFIG.introductoryPhrasePattern.test(text)) return;
      const extracted = extractConditionFromText(text);
      if (extracted) {
        candidates.push({
          start,
          end,
          html: segment,
          ...extracted
        });
      }
    };

    while ((match = separatorPattern.exec(html))) {
      pushSegment(match.index);
      start = match.index + match[0].length;
    }
    pushSegment(html.length);

    return candidates;
  };

  const rangesOverlap = (a, b) => a.start < b.end && b.start < a.end;

  const conditionCandidates = (html) => {
    const blockCandidates = collectBlockCandidates(html);
    const lineCandidates = collectLineCandidates(html)
      .filter((candidate) => !blockCandidates.some((block) => rangesOverlap(candidate, block)));
    const seenRanges = new Set();

    return blockCandidates.concat(lineCandidates)
      .filter((candidate) => {
        const key = candidate.start + ':' + candidate.end;
        if (seenRanges.has(key)) return false;
        seenRanges.add(key);
        return true;
      })
      .sort((a, b) => a.start - b.start || a.end - b.end);
  };

  const firstMeaningfulCandidate = (html, candidates = conditionCandidates(html)) => {
    const seen = new Set();

    for (const candidate of candidates) {
      const key = cleanSpaces(candidate.rawConditionText).toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      return candidate;
    }

    if (candidates.length) return null;

    const extracted = extractConditionFromText(html);
    return extracted && extracted.rawConditionText ? {
      start: 0,
      end: html.length,
      html,
      ...extracted
    } : null;
  };

  const normalizePunctuation = (value) => cleanSpaces(String(value == null ? '' : value)
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/\u00a0/g, ' ')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/\s*\/+\s*/g, ', ')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/([,;:])(?=\S)/g, '$1 ')
    .replace(/(?:,\s*){2,}/g, ', ')
    .replace(/\s+-\s+/g, ' - ')
    .replace(/^[\s,;:.-]+|[\s,;:.-]+$/g, ''));

  const removePhotoInstructions = (value) => {
    let text = String(value == null ? '' : value);
    let found = false;

    CONFIG.photoInstructionPatterns.forEach((pattern) => {
      text = text.replace(pattern, () => {
        found = true;
        return '';
      });
    });

    text = normalizePunctuation(text)
      .replace(/(?:,\s*){2,}/g, ', ')
      .replace(/^[\s,;:.-]+|[\s,;:.-]+$/g, '');

    return { text, found };
  };

  const capitalizeFirst = (value) => String(value || '').replace(/^([a-z])/, (match) => match.toUpperCase());

  const cleanConditionNote = (rawConditionText = '') => {
    const original = cleanSpaces(decodeEntities(rawConditionText)).replace(/^\*+\s*/, '');
    let text = original;
    let correctionsApplied = 0;

    text = text.replace(CONFIG.introWithSeparatorPattern, '');
    text = normalizePunctuation(text);

    CONFIG.typoCorrections.concat(CONFIG.phraseCorrections).forEach(([pattern, replacement]) => {
      text = text.replace(pattern, (...args) => {
        correctionsApplied += 1;
        return typeof replacement === 'function' ? replacement(...args) : replacement;
      });
    });

    text = normalizePunctuation(text);
    const photo = removePhotoInstructions(text);
    text = photo.text;

    if (!/[a-z0-9]/i.test(text)) {
      return {
        cleanedFullNote: '',
        normalizedDetail: '',
        usedFallback: false,
        hadPhotoInstruction: photo.found,
        correctionsApplied
      };
    }

    const recognized = CONFIG.recognizedConditionPattern.test(text);
    const usedFallback = !recognized;
    text = capitalizeFirst(text);

    return {
      cleanedFullNote: text + ', ' + CONFIG.finalPhotoInstruction,
      normalizedDetail: text,
      usedFallback,
      hadPhotoInstruction: photo.found,
      correctionsApplied
    };
  };

  const removeGenericTileIntro = (value) => {
    let text = normalizePunctuation(value);
    let previous = '';

    while (text && previous !== text) {
      previous = text;
      CONFIG.genericTilePhrases.forEach((pattern) => {
        text = normalizePunctuation(text.replace(pattern, ''));
      });
    }

    return text;
  };

  const summarizeTileDetail = (value) => {
    const text = normalizePunctuation(value);
    if (text.length <= CONFIG.maxTileDetailLength) return text;

    const clauses = text.split(/\s*,\s*/).filter(Boolean);
    const kept = [];
    let length = 0;

    for (const clause of clauses) {
      const nextLength = length + clause.length + (kept.length ? 2 : 0);
      if (nextLength > CONFIG.maxTileDetailLength) break;
      kept.push(clause);
      length = nextLength;
    }

    if (kept.length) return kept.join(', ');

    const words = text.split(/\s+/).filter(Boolean);
    let summary = '';
    for (const word of words) {
      const next = summary ? summary + ' ' + word : word;
      if (next.length > CONFIG.maxTileDetailLength - 3) break;
      summary = next;
    }
    return summary ? summary + '...' : '';
  };

  const lowercaseSentenceStart = (value) => String(value || '').replace(/^([A-Z])([a-z])/, (match, first, second) =>
    first.toLowerCase() + second
  );

  const makeTileDetail = (cleanedFullNote = '') => {
    const withoutPhoto = removePhotoInstructions(cleanedFullNote.replace(new RegExp('\\b' + CONFIG.finalPhotoInstruction + '\\b', 'gi'), '')).text;
    let detail = removeGenericTileIntro(withoutPhoto);

    detail = detail
      .replace(/^a\s+(minor|small|light|heavy)\b/i, '$1')
      .replace(/^an\s+/i, '')
      .replace(/\baging\s+on\s+the\s+screen\s+print\b/i, 'screen-print aging');
    detail = normalizePunctuation(detail);

    if (!detail || CONFIG.genericTilePhrases.some((pattern) => pattern.test(detail))) return '';
    return summarizeTileDetail(lowercaseSentenceStart(detail));
  };

  const removeCandidatesFromDescription = (html, candidates) => {
    if (!Array.isArray(candidates) || !candidates.length) return cleanupDescriptionHtml(html);
    const ranges = [];

    candidates
      .filter((candidate) => candidate && typeof candidate.start === 'number' && typeof candidate.end === 'number')
      .sort((a, b) => a.start - b.start || b.end - a.end)
      .forEach((candidate) => {
        if (ranges.some((range) => rangesOverlap(candidate, range))) return;
        ranges.push({ start: candidate.start, end: candidate.end });
      });

    let removed = html;
    ranges.sort((a, b) => b.start - a.start).forEach((range) => {
      removed = removed.slice(0, range.start) + removed.slice(range.end);
    });
    return cleanupDescriptionHtml(removed);
  };

  const parse = (descriptionHtml = '') => {
    const html = String(descriptionHtml == null ? '' : descriptionHtml);
    const candidates = conditionCandidates(html);
    const candidate = firstMeaningfulCandidate(html, candidates);

    if (!candidate) {
      if (candidates.length) {
        return {
          found: true,
          rawMatchedLine: candidates[0].rawMatchedLine || '',
          rawConditionText: '',
          cleanedFullNote: '',
          tileDetail: '',
          descriptionWithoutConditionLine: removeCandidatesFromDescription(html, candidates),
          usedFallback: false,
          hadPhotoInstruction: false,
          correctionsApplied: 0
        };
      }

      return {
        found: false,
        rawMatchedLine: '',
        rawConditionText: '',
        cleanedFullNote: '',
        tileDetail: '',
        descriptionWithoutConditionLine: cleanupDescriptionHtml(html),
        usedFallback: false
      };
    }

    const cleaned = cleanConditionNote(candidate.rawConditionText);
    const cleanedFullNote = cleaned.cleanedFullNote;

    return {
      found: true,
      rawMatchedLine: candidate.rawMatchedLine,
      rawConditionText: candidate.rawConditionText,
      cleanedFullNote,
      tileDetail: cleanedFullNote ? makeTileDetail(cleanedFullNote) : '',
      descriptionWithoutConditionLine: removeCandidatesFromDescription(html, candidates.length ? candidates : [candidate]),
      usedFallback: cleaned.usedFallback,
      hadPhotoInstruction: cleaned.hadPhotoInstruction,
      correctionsApplied: cleaned.correctionsApplied
    };
  };

  const parseConditionNoteText = (rawConditionText = '') => {
    const cleaned = cleanConditionNote(rawConditionText);
    return {
      found: Boolean(cleaned.cleanedFullNote),
      rawMatchedLine: cleanSpaces(rawConditionText),
      rawConditionText: cleanSpaces(rawConditionText),
      cleanedFullNote: cleaned.cleanedFullNote,
      tileDetail: cleaned.cleanedFullNote ? makeTileDetail(cleaned.cleanedFullNote) : '',
      descriptionWithoutConditionLine: '',
      usedFallback: cleaned.usedFallback,
      hadPhotoInstruction: cleaned.hadPhotoInstruction,
      correctionsApplied: cleaned.correctionsApplied
    };
  };

  const api = {
    CONFIG,
    parse,
    parseConditionNoteText,
    cleanConditionNote,
    makeTileDetail,
    stripHtml
  };

  global.VCCConditionNoteParser = api;
  if (typeof global.dispatchEvent === 'function' && typeof global.Event === 'function') {
    global.dispatchEvent(new global.Event('vcc:condition-note-parser-ready'));
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
